// Copyright ©️ 2026 eVolve MEP, LLC

import type { StrongOmit } from '@evolvemep/api-ts';
import * as THREE from 'three';

import { isNil } from 'helpers/isNotNil';
import {
  convertToRadians,
  isInsignificant,
  isSignificant,
} from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/helpers';
import type { AngleLabelOpts } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/AngleLabel';
import { labelTheming } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/labelTheming';
import type { MeasurementLabelOpts } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/Labels/MeasurementLabel';

import type { DimensionLabelOpts } from './DimensionLabel';

type BaseLabelDef = Pick<DimensionLabelOpts, 'directionPreference' | 'canPlaceInsideGeometry'> & {
  /** The plain text label to display. */
  label: string;
  /**
   * How far from the anchor point the label is placed. Uses the same unit scale as the
   * underlying NDC layout (0–1 range, where 1 fills the half-screen). The default
   * works well for most labels; increase to push a label further away,
   * or decrease to pull it closer.
   * @defaultValue {@link labelTheming.defaultPreferredDistance}
   */
  preferredDistance?: DimensionLabelOpts['ndcLabelOffset'];
};

export type PlacementOffset =
  | {
      relativeTo: 'bendPlane';
      /**
       * An offset direction for the label, relative to the bend plane before bends are applied
       * and with a magnitude equal to the material radius.
       */
      offset: 'left' | 'right' | 'top' | 'bottom' | 'forward' | 'backward';
    }
  | {
      relativeTo: 'world';
      /**
       * An offset direction for the label, relative to the world.
       * - `'up'`: offset in the positive Y direction, with magnitude equal to the material radius.
       * - `'down'`: offset in the negative Y direction, with magnitude equal to the material radius.
       * - `THREE.Vector3`: Escape hatch. Adds an offset in the specified magnitude and direction.
       */
      offset: 'up' | 'down' | THREE.Vector3;
    };

type LengthBendDefLabel = BaseLabelDef & {
  length: number;
  isVertical?: boolean;
  /** When true, the label will run in the opposite direction of the bend.
   * This is useful for measurements taken relative to the end of the bend.
   * @defaultValue false
   **/
  isReverse?: boolean;
  /**
   * A list of offsets to apply to the label, applied in order.
   */
  offsets?: readonly PlacementOffset[];
};

type AngleBendDefLabel = BaseLabelDef & {
  /** The radius of the arc to draw. */
  radius?: number;
  /** the angle of the arc, in degrees. */
  angle: number;
};

export type BendDefLabel = LengthBendDefLabel | AngleBendDefLabel;

export type LabelOpts =
  | ({ kind: 'angle' } & StrongOmit<AngleLabelOpts, 'group'>)
  | ({ kind: 'measurement' } & StrongOmit<MeasurementLabelOpts, 'group'>);

/** Helper method to shift the label so it can sit along the edge of the bend instead of in the centerline */
const getLabelOffset = (
  offsets: LengthBendDefLabel['offsets'],
  bendToolSim: THREE.Object3D,
  materialRadius: number,
): THREE.Vector3 => {
  if (isNil(offsets)) return new THREE.Vector3();
  const offset = new THREE.Vector3();
  const up = bendToolSim.up.clone().applyQuaternion(bendToolSim.quaternion).normalize();
  const forward = bendToolSim.getWorldDirection(new THREE.Vector3()).normalize();
  const right = new THREE.Vector3().crossVectors(forward, up).normalize();
  offsets.forEach((offsetDef) => {
    if (offsetDef.relativeTo === 'world') {
      if (typeof offsetDef.offset === 'string') {
        offset.add(new THREE.Vector3(0, offsetDef.offset === 'up' ? materialRadius : -materialRadius, 0));
      } else {
        offset.add(offsetDef.offset);
      }
    } else {
      if (offsetDef.offset === 'top' || offsetDef.offset === 'bottom') {
        const toAdd = offsetDef.offset === 'top' ? up : up.clone().negate();
        offset.add(toAdd.clone().multiplyScalar(materialRadius));
      } else if (offsetDef.offset === 'forward' || offsetDef.offset === 'backward') {
        const toAdd = offsetDef.offset === 'forward' ? forward : forward.clone().negate();
        offset.add(toAdd.clone().multiplyScalar(materialRadius));
      } else {
        const toAdd = offsetDef.offset === 'right' ? right : right.clone().negate();
        offset.add(toAdd.clone().multiplyScalar(materialRadius));
      }
    }
  });
  return offset;
};

type BendDataAtLabel = {
  materialRadius: number;
  /**
   * The bend tool simulation object at the label's position.
   * Do not mutate this object — it is shared with the caller and will be reused for subsequent segments.
   */
  bendToolSim: THREE.Object3D;
};

const buildMeasurementLabelOpts = (
  {
    label,
    isVertical,
    isReverse,
    length,
    offsets,
    directionPreference,
    preferredDistance,
    canPlaceInsideGeometry,
  }: LengthBendDefLabel,
  bendData: BendDataAtLabel,
) => {
  const { materialRadius, bendToolSim } = bendData;
  const labelOffset = getLabelOffset(offsets, bendToolSim, materialRadius);
  const start = bendToolSim.position.clone().add(labelOffset);

  // Head down in world space if the label is vertical, otherwise go in the same direction as the bend tool.
  const usingDirection = isVertical
    ? new THREE.Vector3(0, -1, 0)
    : bendToolSim.getWorldDirection(new THREE.Vector3()).normalize();
  if (isReverse) {
    usingDirection.negate();
  }
  const end = start.clone().add(usingDirection.multiplyScalar(length));

  const lengthVec = end.clone().sub(start);
  // This is used to hide labels that don't make sense on one of the orthographic views.
  const isTopViewOnly = isSignificant(lengthVec.x) && isInsignificant(lengthVec.y) && isInsignificant(lengthVec.z);
  const isFrontViewOnly = isSignificant(lengthVec.y) && isInsignificant(lengthVec.x) && isInsignificant(lengthVec.z);

  const layer = isTopViewOnly ? labelTheming.layers.top : isFrontViewOnly ? labelTheming.layers.front : undefined;

  return {
    kind: 'measurement' as const,
    label,
    value: length,
    layer,
    start,
    end,
    directionPreference,
    ndcLabelOffset: preferredDistance,
    canPlaceInsideGeometry,
  };
};

const buildAngleLabelOpts = (
  { label, angle, radius = 1, preferredDistance, directionPreference, canPlaceInsideGeometry }: AngleBendDefLabel,
  { bendToolSim }: BendDataAtLabel,
) => ({
  kind: 'angle' as const,
  label,
  value: angle,
  layer: labelTheming.layers.front,
  center: bendToolSim.position.clone(),
  radius,
  startAngle: 0,
  endAngle: convertToRadians(angle),
  rotation: bendToolSim.quaternion.clone(),
  ndcLabelOffset: preferredDistance,
  directionPreference,
  canPlaceInsideGeometry,
});

/** Converts abstract label definitions to arguments to construct a label. */
export const buildLabelOpts = (labelDef: BendDefLabel, opts: BendDataAtLabel): LabelOpts => {
  if ('length' in labelDef) {
    return buildMeasurementLabelOpts(labelDef, opts);
  }
  return buildAngleLabelOpts(labelDef, opts);
};
